import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  SCO_FISCAL_HOLD_RESOLVE_PERMISSION,
  type ScoFiscalHoldCommandKind,
  type ScoFiscalHoldCommandView,
  type ScoFiscalHoldView,
} from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useHasPermission } from '@/auth/auth-store';
import { useReauthenticate } from '@/hooks/use-export-correction';
import { formatMinor } from '@/hooks/use-shifts';
import { useIssueScoFiscalHold, useScoFiscalHolds } from '@/hooks/use-sco-fiscal-holds';
import { useT } from '@/i18n/use-t';
import {
  ageText,
  cardAtRisk,
  cardStateText,
  fill,
  kindText,
  outcomeText,
  phaseText,
  when,
  type Translate,
} from './fiscal-hold-labels';

type Hold = ScoFiscalHoldView['holds'][number];

/**
 * What the operator saw when choosing (EAT-25 review, PC 7-40469): the stage,
 * the card state, whether the sale is held and voidable, whether a command is
 * already waiting, and whether the report is fresh. A command is sent for
 * exactly this, or not at all: when a re-read shows anything else, the form
 * closes and the operator decides again.
 */
type Seen = {
  phase: Hold['phase'];
  cardState: Hold['cardState'];
  state: Hold['state'];
  voidable: boolean;
  waiting: boolean;
  fresh: boolean;
};
type Choice = { kind: ScoFiscalHoldCommandKind; seen: Seen; token: number };

function seenOf(hold: Hold, fresh: boolean): Seen {
  return {
    phase: hold.phase,
    cardState: hold.cardState,
    state: hold.state,
    voidable: hold.voidable,
    waiting: hold.commands.some((command) => command.completedAt === null),
    fresh,
  };
}

function sameSeen(a: Seen, b: Seen): boolean {
  return (
    a.phase === b.phase &&
    a.cardState === b.cardState &&
    a.state === b.state &&
    a.voidable === b.voidable &&
    a.waiting === b.waiting &&
    a.fresh === b.fresh
  );
}

/** The same gates the Retry / Void buttons apply. */
function offered(kind: ScoFiscalHoldCommandKind, seen: Seen): boolean {
  const open = seen.fresh && seen.state === 'held' && !seen.waiting;
  return kind === 'retry' ? open && seen.phase !== 'commit_unknown' : open && seen.voidable;
}

/** The issue route's refusal codes (EAT-26), in the operator's words. */
export function refusalText(t: Translate, code: string | undefined): string | null {
  switch (code) {
    case 'report_stale':
      return t(
        'terminals.fiscalHold.refusal.reportStale',
        'The lane has not reported in the last 15 minutes. Try again once it reports.',
      );
    case 'hold_not_held':
      return t(
        'terminals.fiscalHold.refusal.holdNotHeld',
        'This sale is already voided and waits for its card reversal; no command acts on it.',
      );
    case 'phase_changed':
      return t(
        'terminals.fiscalHold.refusal.phaseChanged',
        'The sale has moved on since you looked at it. Check it again and decide.',
      );
    case 'not_voidable':
      return t(
        'terminals.fiscalHold.refusal.notVoidable',
        'The lane reports this sale as not voidable now.',
      );
    case 'retry_not_allowed':
      return t(
        'terminals.fiscalHold.refusal.retryNotAllowed',
        'A retry could register this sale twice: its fiscal outcome is unknown.',
      );
    case 'command_open':
      return t(
        'terminals.fiscalHold.refusal.commandOpen',
        'A command for this sale is already waiting for the lane.',
      );
    case 'card_state_changed':
      return t(
        'terminals.fiscalHold.refusal.cardStateChanged',
        'The card payment has changed since you looked at it. Check it again and decide.',
      );
    case 'acknowledgement_required':
      return t(
        'terminals.fiscalHold.refusal.acknowledgementRequired',
        'Confirm that you understand the card payment may be reversed, then void again.',
      );
    case 'hold_not_found':
      return t(
        'terminals.fiscalHold.refusal.holdNotFound',
        'The lane no longer reports this sale as held.',
      );
    case 'terminal_not_found':
    case 'not_sco_lane':
      return t(
        'terminals.fiscalHold.refusal.notALane',
        'This terminal is not a self-checkout lane that holds sales.',
      );
    default:
      return null;
  }
}

function errorCode(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * EAT-25 (EAT-16 slice P2) — a self-checkout lane's sale held for fiscal
 * recovery, as the lane last reported it, with the retry / void commands Back
 * Office sent for it and their outcomes. A holder of `sco-fiscal-hold.resolve`
 * may order one more retry or a void, after confirming the password when the
 * login is older than five minutes (the server's fresh-login rule).
 *
 * A remote void may reverse a card payment already captured, so the card state
 * is shown first, and voiding a captured or unknown card payment needs an
 * explicit acknowledgement.
 *
 * Renders nothing for a lane that never reported, so every other terminal's
 * page is unchanged.
 */
export function FiscalHoldPanel({ terminalId }: { terminalId: string }): JSX.Element | null {
  const t = useT('admin_ui');
  const query = useScoFiscalHolds(terminalId);
  const canResolve = useHasPermission(SCO_FISCAL_HOLD_RESOLVE_PERMISSION);
  const view = query.data;
  if (view === undefined || view.reportedAt === null) return null;

  return (
    <section className="rounded-md border bg-card p-5" data-testid="fiscal-hold-panel">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">
          {t('terminals.fiscalHold.title', 'Held fiscal sale')}
        </h2>
        <span className="text-xs text-muted-foreground" data-testid="fiscal-hold-report-age">
          {t('terminals.fiscalHold.reportedAt', 'Lane reported')} {when(view.reportedAt)}
          {view.reportAgeMs !== undefined && view.reportAgeMs !== null && (
            <>
              {' · '}
              {fill(t('terminals.fiscalHold.reportAge', '{age} ago'), {
                age: ageText(t, view.reportAgeMs),
              })}
            </>
          )}
        </span>
      </div>
      {!view.fresh && (
        <p role="status" className="mb-2 text-xs text-destructive">
          {t(
            'terminals.fiscalHold.stale',
            'The lane has not reported in the last 15 minutes. What is shown may be out of date, and no retry or void can be sent until it reports again.',
          )}
        </p>
      )}
      {view.holds.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('terminals.fiscalHold.empty', 'No sale is held on this lane.')}
        </p>
      ) : (
        <ul className="space-y-3">
          {view.holds.map((hold) => (
            <HoldCard
              key={hold.journalId}
              terminalId={terminalId}
              hold={hold}
              fresh={view.fresh}
              canResolve={canResolve}
              reread={async () => (await query.refetch()).data}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function HoldCard({
  terminalId,
  hold,
  fresh,
  canResolve,
  reread,
}: {
  terminalId: string;
  hold: Hold;
  fresh: boolean;
  canResolve: boolean;
  /** Re-reads the lane view (after a step-up, before the command is resent). */
  reread: () => Promise<ScoFiscalHoldView | undefined>;
}): JSX.Element {
  const t = useT('admin_ui');
  const [choice, setChoice] = useState<Choice | null>(null);
  const [notice, setNotice] = useState<{ text: string; invalidated: boolean } | null>(null);
  // The current form's token. Choosing, closing, cancelling or unmounting
  // moves it on, so an answer that arrives for an earlier form sends nothing.
  const formToken = useRef(0);
  const now = seenOf(hold, fresh);
  const choose = (kind: ScoFiscalHoldCommandKind): void => {
    formToken.current += 1;
    setNotice(null);
    setChoice({ kind, seen: now, token: formToken.current });
  };
  const close = useCallback((): void => {
    formToken.current += 1;
    setChoice(null);
  }, []);
  useEffect(
    () => () => {
      formToken.current += 1;
    },
    [],
  );
  const invalidatedText = t(
    'terminals.fiscalHold.formInvalidated',
    'The sale changed while the form was open, so nothing was sent. Check it again and decide.',
  );
  const invalidate = useCallback((): void => {
    close();
    setNotice({ text: invalidatedText, invalidated: true });
  }, [close, invalidatedText]);
  // A re-read that changes what the operator confirmed on, or takes away a
  // gate the buttons apply (fresh report, no open command, voidable), closes
  // the open form.
  const changed = choice !== null && (!sameSeen(choice.seen, now) || !offered(choice.kind, now));
  useEffect(() => {
    if (changed) invalidate();
  }, [changed, invalidate]);
  const waiting = hold.commands.find((command) => command.completedAt === null);
  const held = hold.state === 'held';
  const open = fresh && held && waiting === undefined;
  const retryable = open && hold.phase !== 'commit_unknown';
  const voidable = open && hold.voidable;
  const risky = cardAtRisk(hold.cardState);

  return (
    <li className="rounded-md border p-3 text-xs" data-testid={`fiscal-hold-${hold.journalId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold tabular-nums">
          {formatMinor(String(hold.totalMinor), hold.currency)}
        </span>
        <span className="text-muted-foreground">
          {fill(t('terminals.fiscalHold.sale', 'Sale {journal} · held since {since}'), {
            journal: hold.journalId,
            since: when(hold.since),
          })}
        </span>
      </div>

      <p
        data-testid="fiscal-hold-card-state"
        className={
          risky
            ? 'mt-2 rounded-md border border-destructive p-2 font-medium text-destructive'
            : 'mt-2 rounded-md border p-2 font-medium'
        }
      >
        {t('terminals.fiscalHold.cardState', 'Card payment')}: {cardStateText(t, hold.cardState)}
      </p>

      {held && hold.notBegunSince !== undefined && (
        // EAT-25 (MICRO-57 follow-up): the fiscal device refuses the sale
        // before its receipt begins, which spends no automatic retry.
        <p data-testid="fiscal-hold-not-begun" className="mt-2 font-medium text-destructive">
          {fill(t('terminals.fiscalHold.notBegun', 'Not begun since {since}, {count} refusals'), {
            since: when(hold.notBegunSince),
            count: String(hold.notBegunCount ?? 0),
          })}
        </p>
      )}

      {!held && (
        <p role="alert" className="mt-2 text-destructive">
          {t(
            'terminals.fiscalHold.reversalPending',
            'Voided. The card reversal is still outstanding on the lane, which stays blocked until it is done. No command acts on it.',
          )}
        </p>
      )}

      <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">{t('terminals.fiscalHold.stage', 'Stage')}</dt>
        <dd data-testid="fiscal-hold-phase">{phaseText(t, hold.phase)}</dd>
        <dt className="text-muted-foreground">{t('terminals.fiscalHold.retries', 'Retries')}</dt>
        <dd>
          {hold.retryCount}
          {' · '}
          {hold.autoRetriesExhausted ? (
            <span className="font-medium text-destructive" data-testid="fiscal-hold-exhausted">
              {t(
                'terminals.fiscalHold.exhausted',
                'automatic retries exhausted: a person must decide',
              )}
            </span>
          ) : hold.canAutoRetry ? (
            t('terminals.fiscalHold.autoRetrying', 'the lane is still retrying on its own')
          ) : (
            t('terminals.fiscalHold.notAutoRetrying', 'the lane does not retry this on its own')
          )}
        </dd>
        <dt className="text-muted-foreground">{t('terminals.fiscalHold.voidable', 'Voidable')}</dt>
        <dd>
          {hold.voidable
            ? t('terminals.fiscalHold.yes', 'Yes')
            : t('terminals.fiscalHold.no', 'No')}
        </dd>
        <dt className="text-muted-foreground">
          {t('terminals.fiscalHold.lastError', 'Last error')}
        </dt>
        <dd className="font-mono">{hold.lastErrorCode ?? '—'}</dd>
      </dl>

      {hold.commands.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="fiscal-hold-commands">
          {hold.commands.map((command) => (
            <CommandLine key={command.commandId} command={command} />
          ))}
        </ul>
      )}

      {canResolve && held && choice === null && (
        <div className="mt-3 space-y-1">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!retryable}
              onClick={() => choose('retry')}
            >
              {t('terminals.fiscalHold.retry', 'Retry')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!voidable}
              onClick={() => choose('void')}
            >
              {t('terminals.fiscalHold.void', 'Void')}
            </Button>
          </div>
          {waiting !== undefined && (
            <p className="text-muted-foreground">
              {t(
                'terminals.fiscalHold.refusal.commandOpen',
                'A command for this sale is already waiting for the lane.',
              )}
            </p>
          )}
          {hold.phase === 'commit_unknown' && (
            <p className="text-muted-foreground">
              {t(
                'terminals.fiscalHold.retryBlocked',
                'Retry is not offered: the fiscal outcome is unknown, and a retry could register the sale twice.',
              )}
            </p>
          )}
          {!hold.voidable && (
            <p className="text-muted-foreground">
              {t(
                'terminals.fiscalHold.refusal.notVoidable',
                'The lane reports this sale as not voidable now.',
              )}
            </p>
          )}
        </div>
      )}

      {canResolve && held && choice !== null && !changed && (
        <ConfirmForm
          key={choice.token}
          terminalId={terminalId}
          journalId={hold.journalId}
          kind={choice.kind}
          seen={choice.seen}
          isCurrent={() => formToken.current === choice.token}
          stillAsSeen={async () => {
            const view = await reread();
            const latest = view?.holds.find((row) => row.journalId === hold.journalId);
            return (
              view !== undefined &&
              latest !== undefined &&
              sameSeen(choice.seen, seenOf(latest, view.fresh))
            );
          }}
          onInvalidated={invalidate}
          onCancel={close}
          onSent={() => {
            close();
            setNotice({
              text: t(
                'terminals.fiscalHold.sent',
                'Sent. The lane applies it on its next report; the outcome appears here.',
              ),
              invalidated: false,
            });
          }}
        />
      )}

      {notice !== null && (
        <p
          role="status"
          className={notice.invalidated ? 'mt-2 text-destructive' : 'mt-2'}
          data-testid={notice.invalidated ? 'fiscal-hold-invalidated' : undefined}
        >
          {notice.text}
        </p>
      )}
    </li>
  );
}

function CommandLine({ command }: { command: ScoFiscalHoldCommandView }): JSX.Element {
  const t = useT('admin_ui');
  let status: string;
  if (command.outcome !== null) {
    status = fill(t('terminals.fiscalHold.command.answered', 'lane answered: {outcome}'), {
      outcome: outcomeText(t, command.outcome),
    });
  } else if (command.deliveredAt !== null) {
    status = fill(
      t('terminals.fiscalHold.command.delivered', 'collected by the lane {at}; no answer yet'),
      { at: when(command.deliveredAt) },
    );
  } else {
    status = fill(
      t('terminals.fiscalHold.command.waiting', 'waiting for the lane to collect it (until {at})'),
      { at: when(command.expiresAt) },
    );
  }
  return (
    <li data-testid={`fiscal-hold-command-${command.commandId}`}>
      <span className="font-medium">{kindText(t, command.kind)}</span> · {command.actor.name} ·{' '}
      {when(command.issuedAt)} · {status}
    </li>
  );
}

function ConfirmForm({
  terminalId,
  journalId,
  kind,
  seen,
  isCurrent,
  stillAsSeen,
  onInvalidated,
  onCancel,
  onSent,
}: {
  terminalId: string;
  journalId: string;
  kind: ScoFiscalHoldCommandKind;
  /** What the operator confirmed on; the command is bound to it. */
  seen: Seen;
  /** False once this form was cancelled, closed, superseded or unmounted. */
  isCurrent: () => boolean;
  /** Re-reads the lane and says whether the hold is still what was confirmed. */
  stillAsSeen: () => Promise<boolean>;
  onInvalidated: () => void;
  onCancel: () => void;
  onSent: () => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const issue = useIssueScoFiscalHold();
  const reauth = useReauthenticate();
  const [acknowledged, setAcknowledged] = useState(false);
  // Read at send time, never from a closure captured before an await (PC 7-40613).
  const acknowledgedNow = useRef(false);
  const [confirmAgain, setConfirmAgain] = useState(false);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One submission at a time, decided synchronously: a double click must not
  // send twice before the pending state renders.
  const busy = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const isVoid = kind === 'void';
  const mustAcknowledge = isVoid && cardAtRisk(seen.cardState);
  const ready = (!mustAcknowledge || acknowledged) && (!needsPassword || password.length > 0);

  async function confirm(): Promise<void> {
    if (busy.current || !isCurrent()) return;
    busy.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (needsPassword) {
        await reauth.mutateAsync(password);
        if (!isCurrent()) return;
        setPassword('');
        setNeedsPassword(false);
        // Time passed while the password was typed: resend only for the hold
        // the operator confirmed on, as the lane reports it now.
        const same = await stillAsSeen();
        if (!isCurrent()) return;
        if (!same) {
          onInvalidated();
          return;
        }
        // A step-up never resends the decision it interrupted: the operator
        // confirms again from the form as it is now (PC 7-40613), so any
        // change to the acknowledgement meanwhile, including untick and
        // re-tick, is decided afresh.
        setConfirmAgain(true);
        return;
      }
      setConfirmAgain(false);
      // Built synchronously from the click: the acknowledgement as it is now.
      const cardAcknowledged = mustAcknowledge && acknowledgedNow.current;
      if (mustAcknowledge && !cardAcknowledged) return;
      await issue.mutateAsync({
        terminalId,
        holdJournalId: journalId,
        kind,
        // The stage the operator decided on; the lane and Back Office both
        // refuse it if the sale has moved on.
        expectedPhase: seen.phase,
        // A void is bound on Back Office to the card state shown and to the
        // acknowledgement (EAT-25 review).
        ...(isVoid ? { expectedCardState: seen.cardState, cardAcknowledged } : {}),
      });
      if (isCurrent()) onSent();
    } catch (cause) {
      if (!isCurrent()) return;
      const code = errorCode(cause);
      if (code === 'reauth_required') {
        setNeedsPassword(true);
        setError(
          t(
            'terminals.fiscalHold.reauthRequired',
            'Confirm your password to send this. A retry or void needs a sign-in in the last 5 minutes.',
          ),
        );
      } else if (code === 'reauth_unavailable') {
        setError(
          t(
            'terminals.fiscalHold.reauthUnavailable',
            'Your account signs in through single sign-on and cannot confirm a password here. Sign out, sign in again, and send it within 5 minutes.',
          ),
        );
      } else {
        setError(
          refusalText(t, code) ??
            t('terminals.fiscalHold.error', 'The command was not sent. Try again.'),
        );
      }
    } finally {
      busy.current = false;
      if (isCurrent()) setSubmitting(false);
    }
  }

  return (
    <div className="mt-3 space-y-2 rounded-md border p-2" data-testid="fiscal-hold-confirm">
      <p className="font-medium">
        {isVoid
          ? t('terminals.fiscalHold.voidConfirm', 'Void this sale on the lane?')
          : t('terminals.fiscalHold.retryConfirm', 'Retry this sale once more on the lane?')}
      </p>
      {isVoid ? (
        <p
          data-testid="fiscal-hold-void-warning"
          className={mustAcknowledge ? 'font-medium text-destructive' : undefined}
        >
          {seen.cardState === 'captured'
            ? t(
                'terminals.fiscalHold.voidCaptured',
                'The card payment was captured. Voiding reverses it on the card terminal: the customer gets the money back and the sale is not recorded.',
              )
            : seen.cardState === 'unknown'
              ? t(
                  'terminals.fiscalHold.voidUnknown',
                  'The lane cannot tell whether the card was charged. Voiding asks the card terminal to reverse any charge. Check the payment with the bank or EPS before you void.',
                )
              : seen.cardState === 'authorised'
                ? t(
                    'terminals.fiscalHold.voidAuthorised',
                    'The card payment is authorised, not captured. Voiding releases the authorisation.',
                  )
                : t('terminals.fiscalHold.voidNoCard', 'There is no card payment on this sale.')}
        </p>
      ) : (
        <p>
          {t(
            'terminals.fiscalHold.retryExplain',
            'The lane tries to fiscalise this sale once more. Its automatic retries are not restarted.',
          )}
        </p>
      )}
      {mustAcknowledge && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => {
              acknowledgedNow.current = event.target.checked;
              setAcknowledged(event.target.checked);
            }}
          />
          {t(
            'terminals.fiscalHold.acknowledgeCard',
            'I understand the card payment may be reversed',
          )}
        </label>
      )}
      {needsPassword && (
        <div className="space-y-1">
          <Label htmlFor={`fiscal-hold-password-${journalId}`}>
            {t('terminals.fiscalHold.password', 'Your password')}
          </Label>
          <input
            id={`fiscal-hold-password-${journalId}`}
            type="password"
            autoComplete="current-password"
            className="w-full rounded-md border border-border bg-background p-1 text-xs"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
      )}
      {confirmAgain && (
        <p role="status" data-testid="fiscal-hold-confirm-again">
          {t(
            'terminals.fiscalHold.confirmAgain',
            'Password confirmed. Check the form and confirm again to send.',
          )}
        </p>
      )}
      {error !== null && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" disabled={!ready || submitting} onClick={() => void confirm()}>
          {isVoid
            ? t('terminals.fiscalHold.confirmVoid', 'Void the sale')
            : t('terminals.fiscalHold.confirmRetry', 'Send the retry')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('terminals.fiscalHold.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}
